import axios from "axios";
import apiClient, { configureApiClientAuth } from "./apiClient";
import { buildLogoutUrl } from "../components/shared/utils/logoutRedirect";
import {
  beginNewAuthSession,
  hasCompletedProfileBootstrap,
  markProfileBootstrapComplete,
  resetAuthSessionState,
} from "../components/shared/utils/authSessionState";

const AUTH_PATH = "/api/v3/auth";
const REFRESH_INTERVAL = 25 * 60 * 1000; // 25 minutes
const REFRESH_RETRY_BASE_DELAY = 30 * 1000; // 30 seconds
const REFRESH_RETRY_MAX_DELAY = 5 * 60 * 1000; // 5 minutes
const REFRESH_LOCK_TTL = 60 * 1000; // 1 minute
const REFRESH_LOCK_RECHECK_DELAY = 10 * 1000; // 10 seconds
const REFRESH_LAST_SUCCESS_KEY = "mayaLastSuccessfulTokenRefreshAt";
const REFRESH_LOCK_KEY = "mayaTokenRefreshLock";
const REFRESH_TAB_ID = `refresh-${Date.now()}-${Math.random()
  .toString(36)
  .slice(2, 10)}`;

let signInWithTokenRequest = null;
let profileBootstrappedInCurrentTab = false;
let refreshTimeoutId = null;
let refreshSchedulerActive = false;
let refreshListenersAttached = false;
let refreshInFlight = false;
let refreshRequestPromise = null;
let consecutiveRefreshFailures = 0;
let authInvalidationInProgress = false;

const normalizeApiError = (error, fallbackMessage = "Request failed") => {
  const responseData = error?.response?.data;
  const status =
    error?.response?.status ??
    responseData?.status ??
    error?.status ??
    responseData?.code ??
    error?.code;
  const message =
    responseData?.error ||
    responseData?.message ||
    error?.message ||
    fallbackMessage;

  const normalizedError = new Error(message);
  normalizedError.name = "ApiError";

  if (status !== undefined) {
    normalizedError.status = status;
  }

  if (responseData?.code !== undefined) {
    normalizedError.code = responseData.code;
  } else if (status !== undefined) {
    normalizedError.code = status;
  }

  if (error?.response) {
    normalizedError.response = error.response;
  } else if (responseData) {
    normalizedError.response = {
      status,
      data: responseData,
    };
  }

  normalizedError.data = responseData ?? error?.data ?? error;

  return normalizedError;
};

const handleRequest = async (method, path, data, config = {}) => {
  try {
    const url = `${AUTH_PATH}${path}`;

    let response;
    if (method === "get" || method === "delete") {
      response = await apiClient[method](url, config);
    } else {
      response = await apiClient[method](url, data, config);
    }

    return response.data;
  } catch (error) {
    console.error(`Auth ${method.toUpperCase()} request failed:`, error);
    throw normalizeApiError(error, "Auth request failed");
  }
};

export const loginApi = (credentials) =>
  handleRequest("post", "/login", credentials);

export const sendVerificationEmailApi = (email) =>
  handleRequest("post", "/sendVerificationEmail", { email });

// export const signInWithToken = async (_tokenData) => {
//   if (!signInWithTokenRequest) {
//     signInWithTokenRequest = handleRequest(
//       "get",
//       "/fetchProfileData?target=console",
//     ).finally(() => {
//       signInWithTokenRequest = null;
//     });
//   }

//   return signInWithTokenRequest;
// };

export const signInWithToken = async (_tokenData, { force = false } = {}) => {
  if (profileBootstrappedInCurrentTab && !force) {
    return null;
  }

  if (!signInWithTokenRequest) {
    signInWithTokenRequest = handleRequest(
      "get",
      "/fetchProfileData?target=console",
    )
      .then((response) => {
        profileBootstrappedInCurrentTab = true;
        return response;
      })
      .finally(() => {
        signInWithTokenRequest = null;
      });
  }

  return signInWithTokenRequest;
};
// export const bootstrapAuthenticatedSession = async ({
//   isFreshLogin = false,
//   tokenData,
// } = {}) => {
//   if (isFreshLogin) {
//     signInWithTokenRequest = null;
//     beginNewAuthSession();
//   }

//   const response = await signInWithToken(tokenData);
//   markProfileBootstrapComplete();

//   return response;
// };

export const bootstrapAuthenticatedSession = async ({
  isFreshLogin = false,
  tokenData,
} = {}) => {
  if (isFreshLogin) {
    signInWithTokenRequest = null;
    profileBootstrappedInCurrentTab = false;
    beginNewAuthSession();
  }

  const response = await signInWithToken(tokenData);

  if (response) {
    markProfileBootstrapComplete();
  }

  return response;
};

export const ensureAuthenticatedSessionBootstrapped = async ({
  tokenData,
} = {}) => {
  if (hasCompletedProfileBootstrap()) {
    return null;
  }

  return bootstrapAuthenticatedSession({ tokenData });
};

export const signUp = (userData) => handleRequest("post", "/signup", userData);

export const getGoogleUserProfile = async (token) =>
  axios.get("https://www.googleapis.com/oauth2/v3/userinfo", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

export const getMicrosoftUserProfile = async (accessToken) => {
  try {
    const response = await axios.get("https://graph.microsoft.com/v1.0/me", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Microsoft user profile:", error);
    throw error;
  }
};

export const logoutApi = async () => {
  try {
    const response = await apiClient.post(`${AUTH_PATH}/logout`);
    return response.data;
  } catch (error) {
    console.error("Error logging out:", error);
    throw normalizeApiError(error, "Logout failed");
  }
};

const safeSessionStorageRead = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionStorageWrite = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage issues.
  }
};

const safeSessionStorageRemove = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore storage issues.
  }
};

const getErrorStatus = (error) =>
  error?.response?.status ?? error?.status ?? error?.code;

const isHardRefreshFailure = (error) => {
  const status = getErrorStatus(error);
  return status === 401 || status === 403 || status === 435;
};

const isRetryableRefreshFailure = (error) => {
  const status = getErrorStatus(error);

  if (!status) {
    return true;
  }

  return status === 408 || status === 425 || status === 429 || status >= 500;
};

const getStoredRefreshTimestamp = () => {
  const rawValue = safeSessionStorageRead(REFRESH_LAST_SUCCESS_KEY);
  const timestamp = Number(rawValue);

  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
};

const markRefreshSuccess = (timestamp = Date.now()) => {
  consecutiveRefreshFailures = 0;
  safeSessionStorageWrite(REFRESH_LAST_SUCCESS_KEY, String(timestamp));
  return timestamp;
};

const getRefreshRetryDelay = () =>
  Math.min(
    REFRESH_RETRY_BASE_DELAY * 2 ** Math.max(0, consecutiveRefreshFailures - 1),
    REFRESH_RETRY_MAX_DELAY,
  );

const getDelayUntilNextRefresh = () => {
  const lastSuccessfulRefreshAt = getStoredRefreshTimestamp();

  if (lastSuccessfulRefreshAt === null) {
    return 0;
  }

  const elapsed = Date.now() - lastSuccessfulRefreshAt;
  return Math.max(0, REFRESH_INTERVAL - elapsed);
};

const readRefreshLock = () => {
  const rawLock = safeSessionStorageRead(REFRESH_LOCK_KEY);

  if (!rawLock) {
    return null;
  }

  try {
    const parsedLock = JSON.parse(rawLock);
    const ownerId = String(parsedLock?.ownerId || "");
    const expiresAt = Number(parsedLock?.expiresAt);

    if (!ownerId || !Number.isFinite(expiresAt)) {
      safeSessionStorageRemove(REFRESH_LOCK_KEY);
      return null;
    }

    return { ownerId, expiresAt };
  } catch {
    safeSessionStorageRemove(REFRESH_LOCK_KEY);
    return null;
  }
};

const acquireRefreshLock = () => {
  if (typeof window === "undefined") {
    return true;
  }

  const existingLock = readRefreshLock();
  const now = Date.now();

  if (
    existingLock &&
    existingLock.ownerId !== REFRESH_TAB_ID &&
    existingLock.expiresAt > now
  ) {
    return false;
  }

  safeSessionStorageWrite(
    REFRESH_LOCK_KEY,
    JSON.stringify({
      ownerId: REFRESH_TAB_ID,
      expiresAt: now + REFRESH_LOCK_TTL,
    }),
  );

  const writtenLock = readRefreshLock();
  return writtenLock ? writtenLock.ownerId === REFRESH_TAB_ID : true;
};

const releaseRefreshLock = () => {
  const existingLock = readRefreshLock();

  if (existingLock?.ownerId === REFRESH_TAB_ID) {
    safeSessionStorageRemove(REFRESH_LOCK_KEY);
  }
};

export const clearTokenRefreshState = () => {
  consecutiveRefreshFailures = 0;
  safeSessionStorageRemove(REFRESH_LAST_SUCCESS_KEY);
  releaseRefreshLock();
};

export const refreshToken = async () => {};

const clearRefreshTimeout = () => {
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
};

const scheduleNextRefresh = (delayMs) => {
  if (!refreshSchedulerActive) return;

  clearRefreshTimeout();

  const nextDelay =
    delayMs ??
    (consecutiveRefreshFailures > 0
      ? getRefreshRetryDelay()
      : getDelayUntilNextRefresh());

  refreshTimeoutId = setTimeout(
    () => {
      void runScheduledRefresh({
        reason:
          consecutiveRefreshFailures > 0 ? "retry-after-failure" : "interval",
      });
    },
    Math.max(0, nextDelay),
  );
};

const invalidateAuthSession = () => {
  profileBootstrappedInCurrentTab = false;
  if (authInvalidationInProgress) return;

  authInvalidationInProgress = true;
  stopTokenRefreshScheduler();
  clearTokenRefreshState();
  resetAuthSessionState();

  try {
    // sessionStorage.clear();
  } catch {
    // Ignore storage issues.
  }

  if (typeof window !== "undefined") {
    window.location.replace(
      buildLogoutUrl({
        reason: "session_expired",
      }),
    );
  }
};

const refreshSession = async ({ reason = "manual" } = {}) => {
  if (refreshRequestPromise) {
    return refreshRequestPromise;
  }

  refreshInFlight = true;
  refreshRequestPromise = (async () => {
    try {
      const refreshResponse = await refreshToken();
      markRefreshSuccess();
      authInvalidationInProgress = false;

      if (refreshSchedulerActive) {
        scheduleNextRefresh();
      }

      return refreshResponse;
    } catch (error) {
      consecutiveRefreshFailures += 1;

      if (refreshSchedulerActive && isRetryableRefreshFailure(error)) {
        scheduleNextRefresh(getRefreshRetryDelay());
      }

      throw error;
    } finally {
      refreshInFlight = false;
      refreshRequestPromise = null;
    }
  })();

  return refreshRequestPromise;
};

const runScheduledRefresh = async ({
  reason = "scheduled",
  force = false,
} = {}) => {
  if (!refreshSchedulerActive) return;

  if (refreshRequestPromise) {
    try {
      await refreshRequestPromise;
    } catch {
      // The in-flight caller already handled scheduling or invalidation.
    }
    return;
  }

  if (
    !force &&
    consecutiveRefreshFailures === 0 &&
    getDelayUntilNextRefresh() > 0
  ) {
    scheduleNextRefresh();
    return;
  }

  if (!acquireRefreshLock()) {
    scheduleNextRefresh(REFRESH_LOCK_RECHECK_DELAY);
    return;
  }

  try {
    await refreshSession({ reason });
    console.log("Token refreshed successfully");
  } catch (error) {
    if (isHardRefreshFailure(error)) {
      invalidateAuthSession();
      return;
    }

    console.warn(`Token refresh failed during ${reason}:`, error);
  } finally {
    releaseRefreshLock();
  }
};

const catchUpRefreshIfDue = ({ force = false } = {}) => {
  if (!refreshSchedulerActive) return;

  if (
    !force &&
    consecutiveRefreshFailures === 0 &&
    getDelayUntilNextRefresh() > 0
  ) {
    return;
  }

  clearRefreshTimeout();
  void runScheduledRefresh({
    reason: force ? "forced-catch-up" : "catch-up",
    force,
  });
};

const handleRefreshOnline = () => {
  catchUpRefreshIfDue({
    force: true,
  });
};

const handleRefreshStorage = (event) => {
  if (!refreshSchedulerActive || !event?.key) return;

  if (event.key === REFRESH_LAST_SUCCESS_KEY) {
    consecutiveRefreshFailures = 0;

    if (!refreshInFlight) {
      scheduleNextRefresh();
    }
  }

  if (event.key === REFRESH_LOCK_KEY && !event.newValue && !refreshInFlight) {
    scheduleNextRefresh();
  }
};

const handleRefreshVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    catchUpRefreshIfDue();
  }
};

const attachRefreshLifecycleListeners = () => {
  if (
    refreshListenersAttached ||
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return;
  }

  window.addEventListener("focus", catchUpRefreshIfDue);
  window.addEventListener("online", handleRefreshOnline);
  window.addEventListener("storage", handleRefreshStorage);
  document.addEventListener("visibilitychange", handleRefreshVisibilityChange);
  refreshListenersAttached = true;
};

const detachRefreshLifecycleListeners = () => {
  if (
    !refreshListenersAttached ||
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return;
  }

  window.removeEventListener("focus", catchUpRefreshIfDue);
  window.removeEventListener("online", handleRefreshOnline);
  window.removeEventListener("storage", handleRefreshStorage);
  document.removeEventListener(
    "visibilitychange",
    handleRefreshVisibilityChange,
  );
  refreshListenersAttached = false;
};

export const restoreAuthenticatedSession = async () => {
  try {
    await refreshSession({
      reason: "startup-restore",
    });
    return true;
  } catch (error) {
    if (isHardRefreshFailure(error)) {
      invalidateAuthSession();
      return false;
    }

    console.warn("Unable to validate persisted auth session yet:", error);
    return true;
  }
};

export const startTokenRefreshScheduler = ({
  resetRefreshClock = false,
} = {}) => {
  if (resetRefreshClock) {
    markRefreshSuccess();
  }

  authInvalidationInProgress = false;

  if (refreshSchedulerActive) {
    scheduleNextRefresh();
    return;
  }

  refreshSchedulerActive = true;
  attachRefreshLifecycleListeners();
  scheduleNextRefresh();
};

export const stopTokenRefreshScheduler = () => {
  refreshSchedulerActive = false;
  clearRefreshTimeout();
  releaseRefreshLock();
  detachRefreshLifecycleListeners();
};

configureApiClientAuth({
  shouldRetryAuthError: (error) => {
    const status = getErrorStatus(error);

    if (status !== 401) {
      return false;
    }
  },
  refreshSession: () =>
    refreshSession({
      reason: "axios-response-retry",
    }),
  onRefreshFailure: (error) => {
    if (isHardRefreshFailure(error)) {
      invalidateAuthSession();
    }
  },
});

export const fetchWithAuthRetry = async (input, init = {}) => {
  const executeRequest = () => fetch(input, init);
  let response = await executeRequest();

  if (response.status !== 401 && response.status !== 435) {
    return response;
  }

  try {
    await refreshSession({
      reason: "fetch-response-retry",
    });
  } catch (error) {
    if (isHardRefreshFailure(error)) {
      invalidateAuthSession();
    }

    throw error;
  }

  response = await executeRequest();

  if (response.status === 401 || response.status === 435) {
    invalidateAuthSession();
  }

  return response;
};
